import { t } from '../i18n';
import { getCurrentLocale } from '../i18n';

/**
 * Compact relative timestamp for conversation lists and message meta —
 * preserves the established visual density ("now", "5m", "3h", "2d",
 * "Yesterday", then a short absolute date) while following the ACTIVE app
 * language: labels come from the common.time catalog keys and the absolute
 * date from Intl with the active locale (never a hard-coded 'en-US').
 * Called at render time, so a language switch re-renders fresh values;
 * nothing formatted is ever persisted and the stored ISO timestamp is
 * never altered.
 */
export function relativeTimeShort(isoDate: string): string {
  const then = new Date(isoDate).getTime();
  if (!Number.isFinite(then)) return '';
  const now = Date.now();
  const diffMs = Math.max(0, now - then);
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return t('time.now');
  if (diffMin < 60) return t('time.minutesShort', { count: diffMin });
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return t('time.hoursShort', { count: diffHr });
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return t('time.yesterday');
  if (diffDay < 7) return t('time.daysShort', { count: diffDay });

  const thenDate = new Date(then);
  const sameYear = thenDate.getFullYear() === new Date(now).getFullYear();
  try {
    return new Intl.DateTimeFormat(getCurrentLocale(), {
      month: 'short',
      day:   'numeric',
      year:  sameYear ? undefined : 'numeric',
    }).format(thenDate);
  } catch {
    return thenDate.toLocaleDateString();
  }
}
